import {Directive, ElementRef, forwardRef, HostListener, Renderer2} from '@angular/core';
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from '@angular/forms';

import {msToTimecode, timecodeToMs} from '../../../object-edit/edit-field-timecode/timecode.utils';

@Directive({
  selector: 'input[appTimecodeValueAccessor]',
  standalone: true,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TimecodeValueAccessorDirective),
      multi: true,
    },
  ],
})
export class TimecodeValueAccessorDirective implements ControlValueAccessor {
  private onChange: (value: number | null) => void = () => {};
  private onTouched: () => void = () => {};

  constructor(
    private readonly elementRef: ElementRef<HTMLInputElement>,
    private readonly renderer: Renderer2
  ) {}

  writeValue(value: unknown): void {
    const displayValue =
      typeof value === 'number' && Number.isFinite(value)
        ? msToTimecode(value)
        : '';

    this.renderer.setProperty(this.elementRef.nativeElement, 'value', displayValue);
  }

  registerOnChange(fn: (value: number | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.renderer.setProperty(this.elementRef.nativeElement, 'disabled', isDisabled);
  }

  @HostListener('input', ['$event.target.value'])
  onInput(rawValue: string): void {
    if (rawValue.trim() === '') {
      this.onChange(null);
      return;
    }

    const milliseconds = timecodeToMs(rawValue);

    // NaN intentionally reaches the control so validateTimecode() can reject it.
    this.onChange(milliseconds ?? Number.NaN);
  }

  @HostListener('blur')
  onBlur(): void {
    this.onTouched();

    const input = this.elementRef.nativeElement;
    const rawValue = input.value ?? '';

    if (rawValue.trim() === '') {
      return;
    }

    const milliseconds = timecodeToMs(rawValue);

    // Preserve invalid text so the user can see and correct it.
    if (milliseconds === null) {
      return;
    }

    this.renderer.setProperty(input, 'value', msToTimecode(milliseconds));
  }
}
